// SHA-256 content hashes of the exact legal-document text the user was shown.
//
// These hashes are computed from the SAME bundled markdown that LegalDocPage
// renders on the public /legal/* routes, so the acceptance record can be tied
// to the precise wording in force at accept time (issue #99, AC #4 — "the exact
// text hash so we can re-prompt when the agreement materially changes").
//
// The version string (CURRENT_LEGAL_VERSION) remains the authoritative
// re-prompt key; the content hash is supplementary provenance recorded
// server-side alongside it.

import { LEGAL_DOCS, type LegalDocKey } from './legalVersions';

import termsMd from './content/terms.md?raw';
import privacyMd from './content/privacy.md?raw';
import eulaMd from './content/eula.md?raw';
import acceptableUseMd from './content/acceptable-use.md?raw';

const RAW_BY_KEY: Record<LegalDocKey, string> = {
  terms: termsMd,
  privacy: privacyMd,
  eula: eulaMd,
  'acceptable-use': acceptableUseMd,
};

async function sha256Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Map of { docKey: 'sha256:<hex>' } for every legal document, computed from the
 * exact text bundled with the app. Returns an empty object if Web Crypto's
 * SubtleCrypto is unavailable (non-secure context) — acceptance still proceeds
 * with the version snapshot, just without the optional hash provenance.
 */
export async function computeLegalContentHashes(): Promise<Record<string, string>> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    return {};
  }
  const entries = await Promise.all(
    LEGAL_DOCS.map(async (d) => {
      const raw = RAW_BY_KEY[d.key] ?? '';
      return [d.key, `sha256:${await sha256Hex(raw)}`] as const;
    })
  );
  return Object.fromEntries(entries);
}
